import React, { useState, useEffect, useMemo } from 'react';
import { Box, useTheme, Typography, Button, Card, CardContent, useMediaQuery, TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { tokens } from '../../theme';
import { useNavigate, useParams } from "react-router-dom";
import useTermService from '../../api/services/termService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { DataGrid } from '@mui/x-data-grid';
import * as image from '../../assets';

const Student_Term_Data = () => {
    const { classId, studentId } = useParams();
    const theme = useTheme();
    const borderColor = theme.palette.mode === 'dark' ? 'white' : 'black';
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [terms, setTerms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const { GetTermsByClassId } = useTermService();

    useEffect(() => {
        const fetchData = async () => {
            if (classId) {
                setIsLoading(true);
                const data = await GetTermsByClassId(classId);
                // Filter only terms with status "PUBLISHED" and sort by createdAt
                const filteredData = (data || []).filter(term => term.status === 'PUBLISHED');
                const sortedData = filteredData.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                setTerms(sortedData);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [classId]);

    // Filter terms based on search query
    const filteredTerms = useMemo(() => {
        if (!searchQuery.trim()) {
            return terms;
        }
        const query = searchQuery.toLowerCase().trim();
        return terms.filter(term =>
            term.termName?.toLowerCase().includes(query)
        );
    }, [terms, searchQuery]);

    const handleViewResults = (termId) => {
        navigate(`/term_student_results/${studentId}/${classId}/${termId}`);
    };

    const handleViewEvaluationReport = (termId) => {
        navigate(`/student_evaluation_report/${termId}/${studentId}`);
    };


    // 🟢 Columns for DataGrid
    const columns = [
        {
            field: 'termName',
            headerName: 'Jina la muhula',
            flex: 1,
            minWidth: 100,
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            minWidth: 60,
        },

        {
            field: 'action',
            headerName: 'TAZAMA MATOKEO & RIPOTI',
            minWidth: 400,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Box
                    display="flex"
                    gap={1}
                    justifyContent="center"
                    mt={1}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleViewResults(params.row.id)}
                    >
                        TAZAMA MATOKEO YA MUHULA
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => handleViewEvaluationReport(params.row.id)}
                    >
                        TAZAMA RIPOTI YA TABIA
                    </Button>
                </Box>
            ),
        }



    ];

    return (
        <Box sx={{ ml: '20px', mr: '20px' }}>
            {/* Header Banner */}
            <Box
                height="auto"
                display="flex"
                justifyContent="space-between"
                border={`1px solid ${borderColor}`}
                mb={2}
                flexWrap="wrap"
            >
                {/* Left */}
                <Box
                    flex={1}
                    display="flex"
                    alignItems="center"
                    borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
                    p={1}
                    justifyContent="center"
                >
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
                        <Box
                            component="img"
                            src={image.visigalogo}
                            alt="logo"
                            sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
                        />
                        <Typography
                            variant="h7"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
                        >
                            St Mary's Junior Seminary (VISIGA SEMINARY)
                        </Typography>
                    </Box>
                </Box>

                {/* Center */}
                <Box
                    flex={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
                    p={2}
                    textAlign="center"
                >
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' } }}
                    >
                        ORODHA YA MUHULA YOTE NA MATOKEO YAKE
                    </Typography>
                </Box>

                {/* Right */}
                <Box
                    flex={1}
                    display="flex"
                    alignItems="center"
                    p={1}
                    justifyContent="center"
                >
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
                        <Box
                            component="img"
                            src={image.cathedral5}
                            alt="arch"
                            sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
                        />
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
                        >
                            Seminary under Archdiocese of Dar es Salaam
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Table */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h4" align="start">
                        ORODHA YA MUHULA - (TAZAMA MATOKEO YA MUHULA HUSIKA)
                    </Typography>

                    {/* Search Field */}
                    <TextField
                        placeholder="Search by term name..."
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                        sx={{
                            minWidth: { xs: '100%', sm: '300px' },
                            maxWidth: { xs: '100%', sm: '400px' },
                        }}
                    />
                </Box>

                {isLoading ? (
                    <LoadingSpinner message="Loading terms..." height="250px" />
                ) : filteredTerms.length > 0 ? (
                    <>
                        {isSmallScreen ? (
                            // Card view for small screens
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {filteredTerms.map((term) => (
                                    <Card
                                        key={term.id}
                                        sx={{
                                            border: `1px solid ${borderColor}`,
                                            borderRadius: 1,
                                            boxShadow: 1,
                                        }}
                                    >
                                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {/* Term Name and Status in one row */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                                                            {term.termName || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                px: 0.75,
                                                                py: 0.25,
                                                                borderRadius: 0.5,
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            {term.status || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                
                                                {/* Action Buttons */}
                                                <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="small"
                                                        fullWidth
                                                        onClick={() => handleViewResults(term.id)}
                                                        sx={{ fontSize: '0.75rem', py: 0.5, minHeight: '32px' }}
                                                    >
                                                        TAZAMA MATOKEO YA MUHULA
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        size="small"
                                                        fullWidth
                                                        onClick={() => handleViewEvaluationReport(term.id)}
                                                        sx={{ fontSize: '0.75rem', py: 0.5, minHeight: '32px' }}
                                                    >
                                                        TAZAMA RIPOTI YA TABIA
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ) : (
                            // DataGrid for larger screens
                            <Box sx={{ height: 500, width: '100%', mt: 2 }}>
                                <DataGrid
                                    rows={filteredTerms}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    pageSize={5}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    sx={{
                                        '& .MuiDataGrid-cell:hover': {
                                            cursor: 'default',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="textSecondary">
                            {searchQuery.trim()
                                ? 'No terms found matching your search.'
                                : 'No published terms available.'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Student_Term_Data;
